/**
 * THE ONE ROW-LIFECYCLE POLICY (catalog B5 / TL-10; rules 96 and 120; owner
 * ruling D8, 2026-09-10: "bounded time" for the translated line is the
 * engine's latency plus 10 000 ms, then 'unavailable').
 *
 * Before this module three surfaces kept three clocks for one row:
 *   - the caption engine retired a settled row 6 s after its last text
 *     (`LINE_TIMEOUT_MS`), whether or not its translation had arrived;
 *   - the overlay declared a pending row 'unavailable' 10 s after the later of
 *     first-pending and its last live word (`CAPTION_PENDING_SLA_MS`);
 *   - the caller's scrollback never expired a pending row at all.
 * So a FINAL foreign row with no words was GONE at 6 s, before the verdict it
 * was owed at 10 s could be declared — and on the caller's tab the same row
 * said "Translating…" for the rest of the call.
 *
 * One policy, three consumers:
 *   RETIRE-AFTER   a row leaves the overlay `retireAfterMs` after its last
 *                  change — but a row still AWAITING its verdict is kept.
 *   VERDICT-AFTER  a pending row is declared unavailable `verdictAfterMs`
 *                  after the LATER of its first pending moment and its last
 *                  engine word — the words define the engine's latency, so
 *                  this IS "engine latency + 10 000 ms".
 *   REVIVE-ON-WORDS a verdict is withdrawn the moment a word arrives after
 *                  the instant it was measured from; the notice never stands
 *                  beside words that are arriving (rule 120: never contradict
 *                  the engine's words).
 *
 * D21: captions/rowOutcome decides these deadlines; captions/rowClock owns
 * their observation history and useRowOutcomes executes the shared timer.
 * Both the overlay and caller tab consume that model. This module retains
 * only the ruled intervals; its former independent clock has been retired.
 */

export const ROW_LIFECYCLE = Object.freeze({
  /** How long a settled row stays after its last change before it retires. */
  retireAfterMs: 6_000,
  /** Engine latency + this = the bound after which a pending row is declared unavailable (D8). */
  verdictAfterMs: 10_000,
  /**
   * The MINIMUM gap between one line leaving and the next (owner, 2026-09-15:
   * "a line should never disappear until two seconds after the one before it").
   * Applied in arrival order by `orderedRetireAt`, so lines leave one at a time,
   * oldest first, at least this far apart — the newest can never leave before an
   * earlier one, and never within this window of it. This is the SINGLE place
   * the stagger lives; the budget consumes the same ordered schedule.
   */
  staggerMs: 2_000,
});
